import { marketingPageHeader, siteOrganizerHeader } from '@h2hspark/contracts';
import {
  Button,
  OrganizerSiteHeader,
  SITE_FOOTER_LINK_CLASS,
  SiteFooter as SharedSiteFooter,
  SiteFooterColumn,
  type SiteFooterLinkRenderer,
  cn,
} from '@h2hspark/ui';
import { ChevronRight, Menu, X } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useMemberSession } from '@/auth/auth-provider';
import { getWebEnvironment } from '@/env';

import { MemberAvatarLink } from '../../member/member-avatar';

import { usePublishedPage, useSiteSettings } from './api';
import type { NavigationItem, NavigationOverride } from './constants';
import {
  isMemberAccountAction,
  isMemberAppPath,
  navigationForPage,
  navigationForSession,
  pageNavigationOverrides,
  siteContactEmailAddress,
  siteContactLocation,
  siteFooterGroups,
  siteFooterNote,
  siteFooterPromo,
  siteFooterText,
  siteName,
  siteNavigation,
  siteSocialLinks,
} from './constants';
import { ContentLink } from './content-link-panel';
import type { ContentData } from './types';

/**
 * The site's chrome — its navigation and its footer — read from `siteSettings` (`CMS-014`).
 *
 * ## Why the chrome is content
 *
 * ADR 022 makes `siteSettings` a content type with exactly one entry rather than a configuration
 * file, so that changing a footer link goes through the same publish step, the same audit trail
 * and the same editor as changing a page. The site half of that bargain is this file: the header
 * and footer read the **published snapshot** and nothing else, so an unpublished edit to the
 * navigation is not on the site (`CMS-014` AC-3, AC-4).
 *
 * ## Why it renders while the settings are still loading
 *
 * With defaults. Every reader in `constants.ts` answers "what did the editor say" and "what does
 * the site show when they said nothing" together, so an empty setting, a slow query and a failed
 * one all land on the same sensible chrome rather than on a header that pops in a second late.
 *
 * The one thing with no default is the row of social links: there is no sensible guess at where
 * H2HSpark is on LinkedIn, and a guess would be a link to somebody else's page. Empty means the
 * row is not rendered.
 *
 * ## The header knows whether you are signed in — 2026-09-21
 *
 * `MEM-043` AC-8, TW 44145091 and TW 44145257. A signed-in member is offered **one way into the
 * app plus their avatar** in place of *Log in* and *Get started*; everything else the editor
 * published is untouched, and a signed-out visitor sees exactly what `MEM-043` AC-6 and the
 * prototype draw. The rule for which entries those two are is `navigationForSession` in
 * `constants.ts`; how a cached public page is told about the session at all is `SiteHeader` below.
 *
 * ## Every address here goes through `ContentLink`
 *
 * A footer link and a social URL are fields an editor types, exactly like a hero's Button link —
 * and `javascript:` in one is exactly as live as `javascript:` in the other. There are no raw
 * anchors in this file, and `every-editor-supplied-url-is-checked.test.ts` scans for one.
 */

/**
 * The slug of the marketing page currently on screen, or `undefined` off one.
 *
 * `/` is `home`; `/for-organizers` is `for-organizers`. Anything with a second segment is not a
 * marketing page — `/case-studies/x` and everything under `/app` — and asks for no page, so the
 * header makes no request on the member app's routes.
 */
function currentPageSlug(pathname: string): string | undefined {
  const trimmed = pathname.replace(/\/+$/u, '');
  if (trimmed === '') return 'home';
  const segments = trimmed.split('/').filter((part) => part !== '');
  if (segments.length !== 1) return undefined;
  return segments[0];
}

/**
 * **How the header draws each entry of a navigation** — as a quiet link, or as the filled or
 * outlined pill — returned as a function so the public header and the member app's signed-out
 * actions (`SiteAccountActions`, TW 44206359) draw *Log in* and *Get started* by one rule.
 */
function entryDrawer(navigation: ReadonlyArray<NavigationItem>): {
  readonly drawnAs: (item: NavigationItem, index: number) => string;
  readonly drawEntry: (item: NavigationItem, index: number) => React.ReactElement;
} {
  // ── Which entries are buttons ──────────────────────────────────────────────────────────────
  //
  // The rule used to be positional: the **last** entry is the site's primary action — "Open the
  // app", then "Get started" — and everything before it is a quiet link. That held while the
  // design's right-hand cluster was one button.
  //
  // The 10 September revision gives it three, and the primary is no longer last: *Log in* as
  // plain text, *Get started* filled, *For event organizers* in an outline. So an entry may now
  // say how it is drawn, and the positional rule survives only as the answer for entries that say
  // nothing — which is every entry published before the field existed, so nothing has to be
  // re-authored to keep rendering as it does today.
  const saidNothing = navigation.every((item) => (item.style ?? '') === '');
  const drawnAs = (item: NavigationItem, index: number): string => {
    const authored = item.style ?? '';
    if (authored !== '') return authored;
    return saidNothing && index === navigation.length - 1 ? 'primary' : 'link';
  };

  const drawEntry = (item: NavigationItem, index: number): React.ReactElement => {
    const style = drawnAs(item, index);
    if (style === 'link') {
      return (
        <ContentLink
          key={`${item.label}-${item.href}`}
          href={item.href}
          className="rounded-md px-3 py-2 text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
        >
          {item.label}
        </ContentLink>
      );
    }
    return (
      <Button
        key={`${item.label}-${item.href}`}
        asChild
        variant={style === 'outline' ? 'outline' : 'default'}
        // The prototype's actions are pills, both the filled one and the outlined one —
        // and the shared button draws them that way now, so the hand-added corner is gone
        // with it.
        //
        // `size="sm"` went with it too (TW 44164674). It resolved to 12px type in the UI
        // face, which is what Brad read as "a size smaller and in the wrong typeface for a
        // brand control"; the design sets both of these at `--text-sm` in `font-brand` 700
        // — `padding:11px 20px` on *Get started*, `10px 16px` on *For event organizers*,
        // one height between them, which is the default size.
        className="whitespace-nowrap"
      >
        <ContentLink href={item.href}>{item.label}</ContentLink>
      </Button>
    );
  };

  return { drawnAs, drawEntry };
}

export function SiteHeader(): React.ReactElement {
  const settings = useSiteSettings();
  const { pathname } = useLocation();
  /**
   * **How a cached public page learns about the session** (`MEM-043` AC-8, TW 44145091).
   *
   * Every marketing page is served from one static bundle that a CDN may cache for everybody, and
   * that must not change: a header rendered on the server for a signed-in member would make the
   * page private, and a `Vary` on a session cookie would make it uncacheable for the anonymous
   * visitor the marketing site exists for.
   *
   * So the session is learned **in the browser, after the page is on screen**. `useMemberSession`
   * reads the access token this origin already holds and asks `/auth/me`; until it answers,
   * `status` is `loading` and this header is the signed-out one — the state the cached HTML is
   * correct for, and the state every visitor who is not signed in stays in. A member sees the
   * signed-out actions for as long as that round trip takes and then sees their own header. That
   * is the trade the alternative cannot make: there is no arrangement in which a cached page is
   * right about a session before it has asked.
   *
   * It is not a *flash of the wrong state* for the visitor the cache is for, and for a member it
   * swaps once, in one direction, and never swaps back — which is why nothing here renders an
   * empty cluster while it waits. A header with a hole in it where the actions belong would be a
   * flash of nothing at all, on every page load, for everybody.
   */
  const { status, session } = useMemberSession();
  /**
   * **The header asks for the page it is above**, so a page can say what it changes about the
   * header (`marketingPage.navigationOverrides`).
   *
   * Fetched here rather than handed down, and that is deliberate: the header is a sibling of the
   * `<Outlet/>` in `MarketingLayout`, so plumbing this would mean a context whose only writer is
   * every page. React Query dedupes it against the request the page itself makes on the same key,
   * so it is one fetch either way.
   */
  const page = usePublishedPage(currentPageSlug(pathname));
  /**
   * **A page may wear the Organizer header instead** (`CMS-025` AC-1, TW 44435875). Asked of the
   * same published page as the overrides, so it is one fetch; until the page answers, the header
   * is the Standard one, which is what every page says when it says nothing.
   */
  if (marketingPageHeader(page.data?.data) === 'organizer') {
    // Only an organizer's sign-in gets the signed-in version (`CMS-026`, Sumit 2026-10-29: "b").
    // A member who is not one keeps the published actions, as does everyone while `/auth/me`
    // has not answered: `session` is `null` until it has.
    return (
      <OrganizerHeaderChrome
        settings={settings.data?.data}
        signedInOrganizer={session?.groups.includes('organizer') === true}
      />
    );
  }
  return (
    <SiteHeaderChrome
      settings={settings.data?.data}
      overrides={pageNavigationOverrides(page.data?.data)}
      signedIn={status === 'authenticated'}
    />
  );
}

/**
 * **The Organizer header on the marketing site** (`CMS-025`), drawn by the shared
 * `OrganizerSiteHeader` the organizer console draws too. Its bar and actions come from the
 * published `siteSettings` with the design's defaults (`siteOrganizerHeader`), and every address
 * goes through `ContentLink`, as the Standard header's do (`CMS-025` AC-4). Sticky, as the Standard
 * header is, so changing a page's header does not change how the page scrolls.
 */
export function OrganizerHeaderChrome({
  settings,
  signedInOrganizer = false,
}: {
  readonly settings: ContentData | undefined;
  /**
   * Whether the reader is a signed-in organizer (`CMS-026`). **Defaults to signed out**, which is
   * what a cached public page is correct about and what `CMS-025` draws.
   */
  readonly signedInOrganizer?: boolean;
}): React.ReactElement {
  const { signedInLabel, ...header } = siteOrganizerHeader(settings);
  // ── A signed-in organizer: one way into the console, then the avatar (`CMS-026` AC-1) ──────
  //
  // As the Standard header does for a member (`MEM-043` AC-8), drawn as the design's filled
  // pill. Its address is the console's home, built from this build's console origin rather than
  // read from content: the `organizer:` allow-list holds only the console's public doors, and a
  // signed-in organizer is past them.
  const actions = signedInOrganizer
    ? [
        {
          label: signedInLabel,
          href: `${getWebEnvironment().organizerUrl.replace(/\/+$/u, '')}/`,
          style: 'primary' as const,
          icon: '',
        },
      ]
    : header.actions;
  return (
    <OrganizerSiteHeader
      {...header}
      actions={actions}
      name={siteName(settings)}
      logoSrc="/brand/logotype-white.png"
      homeHref="/"
      renderLink={renderContentLink}
      className="sticky top-0 z-30"
      accessory={signedInOrganizer ? <MemberAvatarLink /> : undefined}
    />
  );
}

export function SiteHeaderChrome({
  settings,
  overrides = [],
  signedIn = false,
}: {
  readonly settings: ContentData | undefined;
  /** What the page on screen changes about the header. See `navigationForPage`. */
  readonly overrides?: ReadonlyArray<NavigationOverride>;
  /**
   * Whether the reader is a signed-in member (`MEM-043` AC-8). **Defaults to signed out**, which
   * is what a cached public page is correct about and what `MEM-043` AC-6 and the prototype draw.
   */
  readonly signedIn?: boolean;
}): React.ReactElement {
  const navigation = navigationForSession(
    navigationForPage(siteNavigation(settings), overrides),
    signedIn,
  );
  const { drawnAs, drawEntry } = entryDrawer(navigation);

  // ── Which entries are pages and which are actions (TW 44164674) ─────────────────────────────
  //
  // Decided by what an entry is, not where it sits, because the list is authored: a button is an
  // action, and so is a plain link into the member app — *Log in* is drawn as text and is still the
  // way in. Everything else is a page. No new field, so every entry already published groups
  // correctly without re-authoring.
  const entries = navigation.map((item, index) => ({ item, index }));
  const isAction = ({ item, index }: { item: NavigationItem; index: number }): boolean =>
    drawnAs(item, index) !== 'link' || isMemberAppPath(item.href);
  const pages = entries.filter((entry) => !isAction(entry));
  const actions = entries.filter(isAction);

  // ── On a phone the navigation folds into a menu (`MEM-043` AC-9, TW 44188597) ─────────────────
  //
  // Sumit, 2026-09-25: "yes, ok to build hamburger menu." Below `md` the header is the wordmark
  // and a menu button, as the consoles' topbar is (`AppShell` in `@h2hspark/ui`: a ghost icon
  // button, `md:hidden`). The button shows and hides the **same** `<nav>` rather than a second
  // copy of it, so there is one landmark, one list of entries and one rule for drawing them; open,
  // it takes a full row under the wordmark and stacks its two groups. From `md` up the button is
  // gone and the nav is drawn as it always was. Escape and following a link both close it.
  const menuId = React.useId();
  const menuButton = React.useRef<HTMLButtonElement>(null);
  const navRef = React.useRef<HTMLElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      menuButton.current?.focus();
    };
    // Following a link closes the menu, including a same-page anchor that changes no route. A
    // listener rather than an `onClick` on the `<nav>`, which is not itself interactive.
    const onClick = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest('a');
      if (link !== null && navRef.current?.contains(link) === true) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onClick);
    };
  }, [menuOpen]);

  // ── How the open menu looks on a phone (`MEM-043` AC-10, TW 44188597) ────────────────────────
  //
  // Brad drew it once AC-9 was on dev, and Sumit, 2026-09-26: "Go ahead with option A." The pages
  // are full-width rows with a divider and an arrow; the member-app actions are full-width pills,
  // the filled one first (*Get started* over *Log in*, or *Open the app* over the avatar); every
  // other action — as seeded, *For event organizers* — is a line of text under them. The design
  // files draw no phone menu, so the picture on the task is the design.
  //
  // It is a second rendering of the same entries inside the **same** `<nav>`, drawn only while the
  // menu is open and only below `md`; the desktop groups are `hidden md:contents`, so from `md` up
  // the header is exactly what it was, and at any width only one set is visible or announced.
  const memberActions = actions
    .filter(({ item }) => isMemberAppPath(item.href))
    .sort(
      (a, b) =>
        Number(drawnAs(b.item, b.index) === 'primary') -
        Number(drawnAs(a.item, a.index) === 'primary'),
    );
  const otherActions = actions.filter(({ item }) => !isMemberAppPath(item.href));
  const phonePanel = menuOpen ? (
    <div data-menu-panel className="flex w-full flex-col pb-4 md:hidden">
      <ul className="w-full">
        {pages.map(({ item }) => (
          <li key={`${item.label}-${item.href}`} data-menu-row className="border-b border-border">
            <ContentLink
              href={item.href}
              className="flex w-full items-center justify-between py-4 font-brand text-lg font-medium text-foreground"
            >
              {item.label}
              <ChevronRight className="h-5 w-5 text-muted-foreground" aria-hidden />
            </ContentLink>
          </li>
        ))}
      </ul>
      <div data-menu-actions className="mt-6 flex w-full flex-col items-stretch gap-3">
        {memberActions.map(({ item, index }) => (
          <Button
            key={`${item.label}-${item.href}`}
            asChild
            size="lg"
            variant={drawnAs(item, index) === 'primary' ? 'default' : 'outline'}
            className="w-full"
          >
            <ContentLink href={item.href}>{item.label}</ContentLink>
          </Button>
        ))}
        {signedIn ? (
          <div className="flex justify-center">
            <MemberAvatarLink />
          </div>
        ) : null}
      </div>
      {otherActions.map(({ item }) => (
        <p
          key={`${item.label}-${item.href}`}
          data-menu-hosting
          className="mt-6 text-center text-sm text-muted-foreground"
        >
          Hosting an event?{' '}
          <ContentLink href={item.href} className="font-brand font-bold text-primary">
            {item.label}
            <span aria-hidden> →</span>
          </ContentLink>
        </p>
      ))}
    </div>
  ) : null;

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-30 border-b backdrop-blur',
          menuOpen ? 'bg-background md:bg-background/80' : 'bg-background/80',
        )}
      >
        {/* `min-h-16` and vertical padding rather than a fixed `h-16`. The bar carried three links
          and one action and fitted on one row at every width; the revision's six entries do not,
          and a wrapped nav inside a 64px box is a nav whose second row is drawn over the page —
          which is what happened at 375, where two links were clipped out of existence while the
          page itself reported no overflow. The bar is 64px whenever its contents fit, and taller
          when they do not. */}
        <div className="mx-auto flex min-h-16 w-full max-w-site flex-wrap items-center justify-between gap-x-4 gap-y-2 px-6 py-3 sm:px-10">
          {/* The wordmark the design was drawn with, which was handed over in the prototype's
            `assets/` and had never been used. What stood here was the app icon beside the site
            name as text — legible, and not the brand: the real mark sets H2H in outline against a
            solid SPARK, with a starburst inside its A. Three rounds of "it does not look like the
            design" and this was in every screenshot of every one of them.

            The site name stays as the accessible name, so a settings change still renames the
            site everywhere it is read aloud. */}
          <Link to="/" className="flex items-center" aria-label={siteName(settings)}>
            <img
              src="/brand/logotype.png"
              alt=""
              className="h-7 w-auto"
              aria-hidden
              width={1787}
              height={298}
            />
          </Link>
          {/* **Three groups, as the design draws them** (TW 44164674): the logotype, the content
            links, and the account actions — `Web App.dc.html`'s top nav is `space-between` over
            exactly those three. The links used to share one cluster with the actions, pushed right,
            so nothing told a page from a way in. Centring the pages in the space between the
            logotype and the actions is what `space-between` over three children does.

            Still **one** `<nav>`: the grouping is visual, and two landmarks would announce the same
            menu twice. `flex-wrap` throughout, because the prototype's own nav is
            `white-space:nowrap` and simply overflows below about 900px, which its author never had
            to look at (`handoff-corrections.md` #119 — the file draws no breakpoints). Below `md`
            the whole nav folds behind the menu button (`MEM-043` AC-9, TW 44188597), which is a
            behavioural breakpoint and so the `site-header-menu` entry in `responsive-behaviour.ts`. */}
          <Button
            ref={menuButton}
            type="button"
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <X className="h-5 w-5" aria-hidden />
            ) : (
              <Menu className="h-5 w-5" aria-hidden />
            )}
          </Button>
          <nav
            aria-label="Main"
            id={menuId}
            ref={navRef}
            className={cn(
              menuOpen ? 'flex basis-full flex-col items-start pb-2' : 'hidden md:flex',
              'gap-4 md:flex md:flex-1 md:basis-auto md:flex-row md:flex-wrap md:items-center md:justify-end md:pb-0',
            )}
          >
            {phonePanel}
            <div className="hidden md:contents">
              <div
                data-nav-group="pages"
                className="flex flex-col items-start gap-1 sm:gap-2 md:flex-1 md:flex-row md:flex-wrap md:items-center md:justify-center"
              >
                {pages.map(({ item, index }) => drawEntry(item, index))}
              </div>
              <div data-nav-group="actions" className="flex flex-wrap items-center gap-1 sm:gap-2">
                {actions.map(({ item, index }) => drawEntry(item, index))}
                {/* The member's own avatar, at the end of the cluster — the place the member app's
                desktop header gives it, so the two headers agree about where *you* are
                (`MEM-043` AC-8). It draws nothing without a session, so this line is the whole of
                the signed-out difference. */}
                {signedIn ? <MemberAvatarLink className="ml-1 sm:ml-2" /> : null}
              </div>
            </div>
          </nav>
        </div>
      </header>
      {/* The page behind the open menu, dimmed (`MEM-043` AC-10). Outside the header, because the
        header's `backdrop-blur` makes it the containing block of anything `fixed` inside it.
        Tapping it closes the menu; it is decoration to a screen reader, which has the button and
        Escape. */}
      {menuOpen ? (
        <div
          data-menu-scrim
          aria-hidden
          className="fixed inset-0 z-20 bg-foreground/30 md:hidden"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * **The public header's two account actions, on their own** — *Log in* and *Get started*, for the
 * member app's header when nobody is signed in (`MEM-043` AC-6, TW 44206359).
 *
 * Brad, on dev: a signed-out visitor on Discover or an event page saw the app header with nothing
 * at the right, because the only thing in that spot is the member's avatar and the avatar draws
 * nothing without a session. The public header already offers both actions; this draws **the same
 * entries by the same rule** — read from the published navigation (so an editor's rewording
 * reaches both headers), recognised by where they go (`isMemberAccountAction`), and drawn by
 * `entryDrawer` — rather than a second pair of buttons that would drift from the first.
 *
 * Styles are resolved against the whole navigation, as the public header resolves them, so the
 * entry that is the filled pill there is the filled pill here. An editor who has deleted both
 * actions gets nothing drawn, for the reason `navigationForSession` gives.
 */
export function SiteAccountActions({
  className,
}: {
  readonly className?: string;
}): React.ReactElement {
  const settings = useSiteSettings();
  const navigation = siteNavigation(settings.data?.data);
  const { drawEntry } = entryDrawer(navigation);
  return (
    <div data-nav-group="actions" className={className ?? 'flex items-center gap-1 sm:gap-2'}>
      {navigation.map((item, index) =>
        isMemberAccountAction(item.href) ? drawEntry(item, index) : null,
      )}
    </div>
  );
}

export function SiteFooter(): React.ReactElement {
  const settings = useSiteSettings();
  return <SiteFooterChrome settings={settings.data?.data} />;
}

/**
 * **One footer, three hosts** — the marketing site, the member app on desktop, and (through the shared
 * `SiteFooter` in `@h2hspark/ui`, whose markup this was) the organizer entry page.
 *
 * `Web App.dc.html` (10 September) draws the member app's footer as *this* footer: the same
 * `--purple-800` band, the white wordmark, the tagline, the social row, the `1.6fr 1fr 1fr 1fr`
 * columns on a 1200px / 40px container and the same copyright rule — with two differences. Its
 * **Members** column holds the app's own destinations and *Log out* rather than the site's
 * anchors, and there is **no organizer pitch** between the columns and the rule. So the member
 * app renders this component with its own columns and without the promo, rather than a second
 * footer that would drift from the first one line at a time (`member/member-footer.tsx`).
 *
 * `columns` replaces the CMS link groups when it is given; everything else — the name, the
 * tagline, the social links, the note, the address and the location — is still read from
 * `siteSettings`, so an edit in the console still reaches both footers (`CMS-014`, `CMS-017`
 * AC-4). Every link inside `columns` is expected to be a {@link FooterLinkItem}, which renders
 * through `ContentLink` like every other address in this file.
 */
export function SiteFooterChrome({
  settings,
  columns,
  withPromo = true,
}: {
  readonly settings: ContentData | undefined;
  /** The link columns, in place of the CMS groups. Built from {@link FooterColumn}. */
  readonly columns?: React.ReactNode;
  /** The organizer pitch between the columns and the rule. The member app's footer has none. */
  readonly withPromo?: boolean;
}): React.ReactElement {
  const promo = withPromo ? siteFooterPromo(settings) : null;

  return (
    <SharedSiteFooter
      name={siteName(settings)}
      logoSrc="/brand/logotype-white.png"
      /* The wordmark goes home (TW 44216821). A plain `/` on this app's own domain is the marketing
         homepage from the marketing site and from the member app under `/app` alike. */
      homeHref="/"
      footerText={siteFooterText(settings)}
      socialLinks={siteSocialLinks(settings)}
      note={siteFooterNote(settings)}
      emailAddress={siteContactEmailAddress(settings)}
      location={siteContactLocation(settings)}
      renderLink={renderContentLink}
      columns={
        columns ??
        siteFooterGroups(settings).map((group) => (
          <FooterColumn key={group.heading} heading={group.heading}>
            {group.links.map((item) => (
              <FooterLinkItem
                key={`${item.label}-${item.href}`}
                href={item.href}
                label={item.label}
              />
            ))}
          </FooterColumn>
        ))
      }
      promo={
        promo === null ? null : (
          /* The organizer pitch, where `Web App.dc.html` puts it: inside the footer block, after
             the link columns and above the copyright rule. */
          <div className="mt-10 flex flex-col gap-6 rounded-xl bg-white/[0.08] p-7 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
            <div>
              {promo.eyebrow !== '' && (
                <p className="font-brand text-[11px] font-bold uppercase tracking-[0.06em] text-fuchsia-200">
                  {promo.eyebrow}
                </p>
              )}
              <h2 className="mt-1 text-2xl font-bold text-primary-foreground">{promo.headline}</h2>
              {promo.body !== '' && (
                <p className="mt-2 max-w-[520px] text-md text-primary-foreground/85">
                  {promo.body}
                </p>
              )}
            </div>
            {(promo.ctaLabel !== '' || promo.secondaryLabel !== '') && (
              <div className="flex flex-none flex-wrap gap-3">
                {promo.ctaLabel !== '' && promo.ctaHref !== '' && (
                  <Button asChild className="rounded-pill bg-white text-brand hover:bg-white/90">
                    <ContentLink href={promo.ctaHref}>{promo.ctaLabel}</ContentLink>
                  </Button>
                )}
                {promo.secondaryLabel !== '' && promo.secondaryHref !== '' && (
                  <Button
                    asChild
                    variant="outline"
                    className="rounded-pill border-white/40 bg-transparent text-primary-foreground hover:bg-white/10"
                  >
                    <ContentLink href={promo.secondaryHref}>{promo.secondaryLabel}</ContentLink>
                  </Button>
                )}
              </div>
            )}
          </div>
        )
      }
    />
  );
}

/**
 * Every address the shared footer draws goes through `ContentLink` — the `safeHref` allow-list and
 * the `organizer:` resolution — which is why the footer takes a renderer rather than writing hrefs.
 */
const renderContentLink: SiteFooterLinkRenderer = ({ href, className, children }) => (
  <ContentLink href={href} className={className}>
    {children}
  </ContentLink>
);

/** How a footer link reads on the band. The shared footer's, re-exported under this app's name. */
export const FOOTER_LINK_CLASS = SITE_FOOTER_LINK_CLASS;

/** One column of the footer. The shared footer's, re-exported under this app's name. */
export const FooterColumn = SiteFooterColumn;

/** One link in a {@link FooterColumn}, through `ContentLink` like every address in this file. */
export function FooterLinkItem({
  href,
  label,
}: {
  readonly href: string;
  readonly label: string;
}): React.ReactElement {
  return <li>{renderContentLink({ href, className: FOOTER_LINK_CLASS, children: label })}</li>;
}
